export interface ScaleInfo {
  type: '1-5' | '1-10' | '1-100' | 'custom';
  min: number;
  max: number;
  confidence: number;
  samples: number[];
}

export class ScaleDetector {
  /**
   * اكتشاف نوع المقياس المستخدم في البيانات
   */
  static detectScale(data: any[], questionColumns: string[]): ScaleInfo {
    const allValues: number[] = [];
    
    // جمع جميع القيم الرقمية من أعمدة الأسئلة
    for (const row of data.slice(0, 50)) { // عينة من 50 صف
      for (const column of questionColumns) {
        const value = row[column];
        if (typeof value === 'number' && !isNaN(value) && value > 0) {
          allValues.push(value);
        }
      }
    }
    
    if (allValues.length === 0) {
      return {
        type: 'custom',
        min: 1,
        max: 5,
        confidence: 0,
        samples: []
      };
    }
    
    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    const uniqueValues = [...new Set(allValues)].sort((a, b) => a - b);
    
    console.log('🔍 تحليل المقياس:', {
      min,
      max,
      uniqueValues: uniqueValues.slice(0, 10),
      totalSamples: allValues.length
    });
    
    // تحديد نوع المقياس بناءً على القيم
    let type: ScaleInfo['type'] = 'custom';
    let confidence = 0;
    
    if (max <= 5 && min >= 1) {
      // مقياس 1-5
      type = '1-5';
      confidence = uniqueValues.every(v => Number.isInteger(v)) ? 0.9 : 0.7;
    } else if (max <= 10 && min >= 1) {
      // مقياس 1-10
      type = '1-10';
      confidence = uniqueValues.every(v => Number.isInteger(v)) ? 0.9 : 0.7;
    } else if (max <= 100 && min >= 1) {
      // مقياس 1-100 (نسب مئوية)
      type = '1-100';
      confidence = max > 50 ? 0.8 : 0.6; // إذا كان هناك قيم عالية، غالباً نسب مئوية
    }
    
    return {
      type,
      min,
      max,
      confidence,
      samples: allValues.slice(0, 20)
    };
  }
  
  /**
   * تحديد مستوى الأداء بناءً على نوع المقياس
   */
  static determinePerformanceLevel(average: number, scaleInfo: ScaleInfo): string {
    switch (scaleInfo.type) {
      case '1-100':
        if (average >= 90) return 'ممتاز';
        if (average >= 80) return 'جيد جدا';
        if (average >= 70) return 'جيد';
        if (average >= 60) return 'مقبول';
        return 'ضعيف';
        
      case '1-10':
        if (average >= 9) return 'ممتاز';
        if (average >= 8) return 'جيد جدا';
        if (average >= 7) return 'جيد';
        if (average >= 6) return 'مقبول';
        return 'ضعيف';
        
      case '1-5':
      default:
        if (average >= 4.5) return 'ممتاز';
        if (average >= 4.0) return 'جيد جدا';
        if (average >= 3.5) return 'جيد';
        if (average >= 3.0) return 'مقبول';
        return 'ضعيف';
    }
  }
  
  /**
   * التحقق من صحة القيم قبل المعالجة
   */
  static validateValue(value: any, scaleInfo: ScaleInfo): number | null {
    if (typeof value === 'number' && !isNaN(value)) {
      // التحقق من أن القيمة ضمن النطاق المتوقع
      if (value >= scaleInfo.min && value <= scaleInfo.max) {
        return value;
      } else {
        console.warn(`⚠️ قيمة خارج النطاق المتوقع: ${value} (النطاق: ${scaleInfo.min}-${scaleInfo.max})`);
        return null;
      }
    }
    return null;
  }
  
  /**
   * تحويل آمن للقيم (إذا لزم الأمر)
   */
  static safeNormalize(value: number, fromScale: ScaleInfo, toScale: ScaleInfo): number {
    if (fromScale.type === toScale.type) {
      return value; // لا حاجة للتحويل
    }
    
    // تحويل آمن بين المقاييس
    const normalizedRatio = (value - fromScale.min) / (fromScale.max - fromScale.min);
    const convertedValue = normalizedRatio * (toScale.max - toScale.min) + toScale.min;
    
    console.log(`🔄 تحويل آمن: ${value} (${fromScale.type}) → ${convertedValue.toFixed(2)} (${toScale.type})`);
    
    return convertedValue;
  }
} 